import React, { useEffect, useState, useRef, useCallback } from "react";
import useRefetchOnFocus from "@/hooks/useRefetchOnFocus";
import Logo from "@/components/Logo";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import track from "@/lib/track";
import Chat from "@/components/Chat";
import CountUp from "@/components/CountUp";
import TarifaEditor from "@/components/TarifaEditor";
import Tour from "@/components/Tour";
import { useMomentos } from "@/context/MomentosContext";

const TOUR_DIAG = [
  { selector: '[data-testid="diag-chat"]', texto: "Contale al asistente lo que te come tiempo. Podés escribir o hablar, como te salga más fácil. Cuando tenga con qué armar tu mapa, lo cierra solo y te lo muestra." },
];

function BarraSuperior() {
  const nav = useNavigate();
  return (
    <header className="h-14 border-b border-borde flex items-center justify-between px-6 shrink-0">
      <Logo variant="mark" height={24} />
      <button onClick={() => nav("/")} className="mono-label text-gris hover:text-crema flex items-center gap-2 transition-colors" data-testid="volver-mesa">
        <ArrowLeft size={14} /> MI MESA
      </button>
    </header>
  );
}

export default function Diagnostico() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const modo = sp.get("agregar") ? "agregar" : null;
  const [focos, setFocos] = useState([]);
  const [cerrado, setCerrado] = useState(false);
  const [totalMostrado, setTotalMostrado] = useState(0);
  const [vhInfo, setVhInfo] = useState(null);
  const [cargando, setCargando] = useState(true);
  const { pedir } = useMomentos();

  // Momento 1: antes de la primera pregunta del diagnóstico.
  useEffect(() => {
    if (!cargando && !cerrado && !modo) pedir("m1");
  }, [cargando, cerrado, modo, pedir]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/focos");
      let fcs = data.focos || [];
      const info = data.valor_hora_info || null;
      const vis = fcs.filter((f) => f.estado !== "resuelto");
      // Si el diagnóstico ya está cerrado (hay mapa y valor de la hora), reabrimos el MAPA, no la charla.
      const listo = !modo && vis.length >= 1 && !!info?.set;
      if (listo) fcs = [...fcs].sort((a, b) => (a.orden_cola ?? 99) - (b.orden_cola ?? 99));
      setFocos(fcs);
      setVhInfo(info);
      if (listo) setCerrado(true);
      setCargando(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Al tocar "agregar algo al mapa" volvemos a la charla.
  useEffect(() => { if (modo === "agregar") setCerrado(false); }, [modo]);

  // El total se recalcula solo cuando cambian los focos o el valor de la hora (ej: lo diste recién en el chat).
  const diagCerradoRef = useRef(false);
  useEffect(() => {
    if (!cerrado) return;
    const vis = focos.filter((f) => f.estado !== "resuelto");
    const total = vis.reduce((s, f) => s + (f.plata_anio || 0), 0);
    setTotalMostrado(total);
    if (vhInfo?.set && !diagCerradoRef.current) {
      diagCerradoRef.current = true;
      const horas = vis.reduce((s, f) => s + (f.horas_mes || 0), 0);
      track("diagnostico_cerrado", { focos: vis.length, horas_totales: Math.round(horas), valor_hora: vhInfo?.valor_hora, plata_anio: Math.round(total) });
    }
    // Momento 2: después de ver el total, antes de la lista de por dónde empezar.
    if (vhInfo?.set) pedir("m2");
  }, [focos, cerrado, vhInfo, pedir]);

  const total = focos.reduce((s, f) => s + (f.plata_anio || 0), 0);
  const vhSet = !!vhInfo?.set;
  const focosVis = focos.filter((f) => f.estado !== "resuelto");

  // Fuente de verdad: relee los focos + el valor de la hora del servidor y repinta.
  // No toca `cerrado` (eso solo se decide en la carga inicial), así no cierra la charla de golpe.
  const recargarFocos = useCallback(async () => {
    try {
      const { data } = await api.get("/focos");
      const fresh = [...(data.focos || [])].sort((a, b) => (a.orden_cola ?? 99) - (b.orden_cola ?? 99));
      setFocos(fresh);
      if (data.valor_hora_info) setVhInfo(data.valor_hora_info);
    } catch (e) { /* noop */ }
  }, []);

  const onAcciones = (data) => {
    if (data?.focos_nuevos?.length) setFocos((prev) => [...prev, ...data.focos_nuevos]);
    if (data?.focos) setFocos(data.focos);
    if (data?.valor_hora_info) setVhInfo(data.valor_hora_info);
    // El asistente guardó algo (ej: el valor de la hora): releemos y el número cambia solo.
    const upd = data?.actualizo || [];
    if (upd.includes("valor_hora") || upd.includes("focos")) recargarFocos();
    // El asistente decidió que la entrevista terminó: pasamos a la pantalla del mapa.
    if (data?.cerrado) { recargarFocos(); setCerrado(true); }
  };

  // Al volver a la pestaña, releer el estado real.
  useRefetchOnFocus(recargarFocos, !cargando);

  // Botón dentro del chat: el asistente invitó a ver el mapa. Cerramos y mostramos el mapa.
  const onAvanzar = async (tipo) => {
    if (tipo !== "cerrar_diagnostico") return;
    try { await api.post("/diagnostico/cerrar"); } catch (e) { /* noop */ }
    await recargarFocos();
    setCerrado(true);
  };

  const onRecalc = (data) => {
    setFocos(data.focos || []);
    if (data.valor_hora_info) setVhInfo(data.valor_hora_info);
  };

  return (
    <div className="h-screen bg-negro flex flex-col">
      <BarraSuperior />
      {cargando ? (
        <div className="flex-1" />
      ) : cerrado ? (
        // DIAGNÓSTICO CERRADO: se ve el MAPA, no la conversación.
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto">
          <div className="max-w-[720px] mx-auto px-8 py-12" data-testid="diag-mapa-cerrado">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-10">
              {vhSet ? (
                <>
                  <div className="text-gris text-lg mb-2">Estás perdiendo</div>
                  <div className="titulo text-[52px] text-grad-dorado leading-none" data-testid="total-perdido">
                    <CountUp value={totalMostrado} duration={1600} prefix="USD " />
                  </div>
                  <div className="text-gris text-lg mt-2">al año</div>
                </>
              ) : (
                <div data-testid="total-sin-valor">
                  <div className="text-gris text-lg mb-2">Ya está tu mapa</div>
                  <p className="text-crema leading-relaxed">Te falta un solo dato para ponerle el número: cuánto vale una hora tuya. Tocá AGREGAR ALGO AL MAPA y te lo pregunto.</p>
                </div>
              )}
            </motion.div>

            {vhSet && <TarifaEditor info={vhInfo} onRecalc={onRecalc} />}

            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }} className="text-gris leading-relaxed mb-10 -mt-4" data-testid="diagnostico-cierre">
              Guardátelo: es la última vez que vas a tener que explicar tu empresa desde cero.
            </motion.p>

            <div className="space-y-3">
              {focosVis.map((f, i) => (
                <motion.div key={f.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} className="card-dark p-5" style={i === 0 ? { border: "1px solid rgba(201,168,76,0.5)" } : {}} data-testid={`foco-orden-${i}`}>
                  {i === 0 && (
                    <div className="mb-2">
                      <div className="mono-label text-dorado">POR ACÁ CONVIENE EMPEZAR</div>
                      <div className="text-gris text-xs mt-1" data-testid="cola-criterio">Ordenado por lo que más rápido podés poner a andar, no solo por horas.</div>
                    </div>
                  )}
                  <div className="flex justify-between items-start gap-3 mb-2">
                    <h3 className="text-crema font-medium">{f.titulo}</h3>
                    <span className="mono-label text-gris shrink-0">N{f.nivel}</span>
                  </div>
                  <div className="flex gap-4">
                    <span className="mono-label text-gris">{f.horas_mes} H/MES</span>
                    {f.plata_mes != null && <span className="mono-label text-dorado">USD {f.plata_mes?.toLocaleString("es-AR")}/MES</span>}
                  </div>
                </motion.div>
              ))}
            </div>

          </div>
          </div>
          {/* Acciones del mapa: SIEMPRE visibles, fijas abajo, sin depender del scroll. */}
          <div className="shrink-0 border-t border-borde" style={{ background: "#0A0A0A" }}>
            <div className="max-w-[720px] mx-auto px-8 py-4 flex flex-col gap-3">
              <button className="btn-dorado w-full" onClick={() => nav("/procesos")} data-testid="ver-cola">VER MI COLA DE PROCESOS</button>
              <button className="btn-fantasma w-full" onClick={() => nav("/diagnostico?agregar=1")} data-testid="agregar-al-mapa">AGREGAR ALGO AL MAPA</button>
            </div>
          </div>
        </div>
      ) : (
        // FASE ENTREVISTA: solo el chat, ancho y centrado. Nada al costado, ni tarjetas ni contador.
        <>
          <Tour id="diagnostico" steps={TOUR_DIAG} />
          <div className="flex-1 flex flex-col min-h-0">
            <div className="w-full max-w-[760px] mx-auto flex-1 flex flex-col min-h-0 px-6 py-6" data-testid="diag-chat">
              <Chat contexto="diagnostico" onAcciones={onAcciones} onAvanzar={onAvanzar} placeholder="Contame, o mandame una captura…" modo={modo} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
